import React, { useState, useEffect, useRef } from 'react';
import BottomNav from '../components/BottomNav';
import { useCompetition } from '../context/CompetitionContext';

export default function Timer() {
  const { categories, teams } = useCompetition();
  const [currentTeam, setCurrentTeam] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [filterCategory, setFilterCategory] = useState('all');
  const [lapTimes, setLapTimes] = useState([]);
  const [pauseProgress, setPauseProgress] = useState(0);
  const intervalRef = useRef(null);
  const pauseTimerRef = useRef(null);
  const pauseStartRef = useRef(null);

  const LONG_PRESS_DURATION = 2000; // 2 seconds

  useEffect(() => {
    if (isRunning && currentTeam) {
      intervalRef.current = setInterval(() => {
        setTimeElapsed(prev => {
          if (prev >= currentTeam.length) {
            setIsRunning(false);
            return currentTeam.length;
          }
          return prev + 0.1;
        });
      }, 100);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, currentTeam]);

  const getBackgroundColor = () => {
    if (!currentTeam) return '#6B7280';
    if (timeElapsed < 30) {
      return '#EF4444';
    } else if (timeElapsed >= currentTeam.length - 30) {
      return '#EF4444';
    } else {
      return '#10B981';
    }
  };

  const getPhaseText = () => {
    if (timeElapsed < 30) {
      return 'FIRST 30 SECONDS';
    } else if (currentTeam && timeElapsed >= currentTeam.length - 30) {
      return 'LAST 30 SECONDS';
    } else {
      return 'MIDDLE SECTION';
    }
  };

  const selectTeam = (team) => {
    setCurrentTeam(team);
    setTimeElapsed(0);
    setIsRunning(false);
  };

  const backToSelection = () => {
    setIsRunning(false);
    setTimeElapsed(0);
    setCurrentTeam(null);
  };

  const getTeamsByCategory = (categoryId) => {
    if (categoryId === 'all') return teams;
    return teams.filter(team => team.category_id === Number(categoryId));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const decimals = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${decimals}`;
  };

  const handleStart = () => {
    setIsRunning(true);
  };

  const handlePause = () => {
    setIsRunning(false);
  };

  const startPauseLongPress = () => {
    pauseStartRef.current = Date.now();
    setPauseProgress(0);

    pauseTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - pauseStartRef.current;
      const progress = Math.min((elapsed / LONG_PRESS_DURATION) * 100, 100);
      setPauseProgress(progress);

      if (elapsed >= LONG_PRESS_DURATION) {
        clearInterval(pauseTimerRef.current);
        pauseTimerRef.current = null;
        setPauseProgress(0);
        handlePause();
      }
    }, 50);
  };

  const cancelPauseLongPress = () => {
    if (pauseTimerRef.current) {
      clearInterval(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
    setPauseProgress(0);
  };

  const handleReset = () => {
    setIsRunning(false);
    setTimeElapsed(0);
    setLapTimes([]);
  };

  const handleLap = () => {
    setLapTimes(prev => [...prev, timeElapsed]);
  };

  // SELECTION SCREEN
  if (!currentTeam) {
    const filteredTeams = getTeamsByCategory(filterCategory);

    return (
      <div className="h-screen max-h-screen bg-gray-100 flex flex-col overflow-hidden">
        <div className="flex-1 min-h-0 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full h-full flex flex-col">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">
              Select Team
            </h1>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Filter by Category:</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              >
                <option value="all">All Categories ({teams.length})</option>
                {categories.map(category => {
                  const count = teams.filter(t => t.category_id === category.id).length;
                  return (
                    <option key={category.id} value={category.id}>
                      {category.name} ({count})
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="space-y-4 mb-6 flex-1 min-h-0 overflow-y-auto">
              {filterCategory === 'all' ? (
                categories.map(category => {
                  const categoryTeams = teams.filter(team => team.category_id === category.id);
                  if (categoryTeams.length === 0) return null;

                  return (
                    <div key={category.id}>
                      <h3 className="text-lg font-bold text-purple-600 mb-2">{category.name}</h3>
                      <div className="space-y-2 ml-2">
                        {categoryTeams.map(team => (
                          <button
                            key={team.id}
                            onClick={() => selectTeam(team)}
                            className="w-full p-4 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 rounded-lg transition-colors text-left"
                          >
                            <div className="flex justify-between items-center">
                              <span className="text-xl font-semibold text-gray-800">{team.name}</span>
                              <span className="text-lg text-gray-600">
                                {Math.floor(team.length / 60)}:{(team.length % 60).toString().padStart(2, '0')}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })
              ) : (
                filteredTeams.map(team => (
                  <button
                    key={team.id}
                    onClick={() => selectTeam(team)}
                    className="w-full p-4 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 rounded-lg transition-colors text-left"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-semibold text-gray-800">{team.name}</span>
                      <span className="text-lg text-gray-600">
                        {Math.floor(team.length / 60)}:{(team.length % 60).toString().padStart(2, '0')}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        <BottomNav />
      </div>
    );
  }

  // TIMER SCREEN
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-2 sm:p-4 transition-colors duration-500"
      style={{ backgroundColor: getBackgroundColor() }}
    >
      <div className="text-center">
        <div className="text-white text-3xl sm:text-5xl font-bold mb-2 sm:mb-4">
          {currentTeam?.name}
        </div>

        <div className="text-white text-lg sm:text-2xl font-bold mb-2 sm:mb-4 opacity-90">
          {getPhaseText()}
        </div>

        <div className="text-white text-5xl sm:text-8xl font-bold mb-4 sm:mb-8 font-mono">
          {formatTime(timeElapsed)}
        </div>

        <div className="text-white text-xl sm:text-3xl mb-6 sm:mb-12">
          / {formatTime(currentTeam?.length || 0)}
        </div>

        <div className="grid grid-cols-2 gap-2 sm:gap-4 justify-items-center max-w-md">
          {!isRunning ? (
            <button
              onClick={handleStart}
              className="w-full px-4 sm:px-6 py-6 sm:py-10 bg-white text-gray-800 text-base sm:text-lg font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              {timeElapsed === 0 ? 'START' : 'RESUME'}
            </button>
          ) : (
            <button
              onMouseDown={startPauseLongPress}
              onMouseUp={cancelPauseLongPress}
              onMouseLeave={cancelPauseLongPress}
              onTouchStart={startPauseLongPress}
              onTouchEnd={cancelPauseLongPress}
              onTouchCancel={cancelPauseLongPress}
              className="w-full px-4 sm:px-6 py-6 sm:py-10 bg-white text-gray-800 text-base sm:text-lg font-bold rounded-lg transition-colors shadow-lg relative overflow-hidden opacity-60"
            >
              <div
                className="absolute inset-0 bg-yellow-400 transition-none"
                style={{ width: `${pauseProgress}%` }}
              />
              <span className="relative z-10">
                {pauseProgress > 0 ? 'HOLD...' : 'PAUSE'}
              </span>
            </button>
          )}

          <button
            onClick={handleLap}
            disabled={timeElapsed === 0}
            className="w-full px-4 sm:px-6 py-6 sm:py-10 bg-white text-gray-800 text-base sm:text-lg font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            INTERVAL
          </button>

          <button
            onClick={handleReset}
            disabled={isRunning}
            className="w-full px-4 sm:px-6 py-6 sm:py-10 bg-white text-gray-800 text-base sm:text-lg font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            RESET
          </button>

          <button
            onClick={backToSelection}
            disabled={isRunning}
            className="w-full px-4 sm:px-6 py-6 sm:py-10 bg-white text-gray-800 text-base sm:text-lg font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            BACK
          </button>
        </div>

        {lapTimes.length > 0 && (
          <div className="mt-4 sm:mt-8 bg-white bg-opacity-20 rounded-lg p-3 sm:p-4 backdrop-blur-sm max-w-md">
            <h3 className="text-white text-lg sm:text-xl font-bold mb-2 sm:mb-3">Interval Times</h3>
            <div className="space-y-2 max-h-40 sm:max-h-60 overflow-y-auto">
              {lapTimes.map((lapTime, index) => (
                <div key={index} className="flex justify-between text-white text-base sm:text-lg font-mono bg-white bg-opacity-10 rounded px-2 sm:px-3 py-1 sm:py-2">
                  <span>Interval {index + 1}</span>
                  <span className="font-bold">{formatTime(lapTime)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 sm:mt-8 text-white text-xs sm:text-sm opacity-75">
          Tap to keep screen awake
        </div>
      </div>
    </div>
  );
}
